// LockedFeatureTease — универсальный empty-state «фича заблокирована тарифом».
// Используется в:
// — panels материала (audio-panel и т.д. для Free) — `entryPoint="material-page"`
// — sidebar / side-panel feature-promo — `entryPoint="sidebar"`
// — route-guard fallback на feature-gated routes — `entryPoint="route"`
//
// Клик на CTA открывает апселл-модалку (`useUpgradeModalStore`) — она же ведёт
// в оплату биллинга экосистемы. Отдельного экрана обоснования больше нет:
// причину замка человек читает прямо здесь, в самом тизере.

import { Button } from "@studyhub/ui/components/button";
import { IconLock } from "@tabler/icons-react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useCommerceVisible } from "@/lib/module-availability/commerce";
import { track } from "@/lib/observability/analytics";
import { useUpgradeModalStore } from "@/stores/upgrade-modal";

/**
 * Ключ платного замка.
 *
 * `module:<id>` — замок целого модуля (`minTier` манифеста, единственный
 * источник состояния `teaser`, декрет §2.8 п.6). Такой замок приходит с готовым
 * текстом (`copy`), потому что копирайт у него общий для всех модулей и живёт в
 * namespace `layout`, а не в billing-каталоге фич.
 */
export type LockedFeatureKey = "audio-recap" | `module:${string}`;
type EntryPoint = "route" | "sidebar" | "material-page";

interface LockedFeatureTeaseProps {
	featureKey: LockedFeatureKey;
	targetTier: "plus" | "pro";
	/**
	 * Точка входа в paywall — пригодится для аналитики (PostHog event) и
	 * мелких UX-вариаций (например, более компактный вид внутри sidebar).
	 */
	entryPoint: EntryPoint;
	className?: string;
	/**
	 * Готовый текст вместо копирайта по `featureKey`.
	 *
	 * Нужен модульному замку: `paywall.feature.<key>.*` — каталог отдельных
	 * платных ФИЧ, и заводить в нём по три строки на каждый из двадцати модулей
	 * значит держать двадцать почти одинаковых текстов. Визуально это тот же
	 * компонент — состояния `teaser` обязаны выглядеть одной системой.
	 */
	copy?: { title: string; description: string; cta: string };
}

export function LockedFeatureTease({
	featureKey,
	targetTier,
	entryPoint,
	className,
	copy,
}: LockedFeatureTeaseProps): React.ReactElement {
	const { t } = useTranslation("billing");
	const commerceVisible = useCommerceVisible();
	// CTA открывает апселл-модалку — одну на приложение, вместе с выбором
	// уровня и переходом в оплату.
	const openUpgrade = useUpgradeModalStore((s) => s.open);

	// PostHog: paywall_shown — mount-once на entryPoint (route/sidebar/material-page).
	// Если компонент перемонтируется при смене entryPoint — это новый показ paywall.
	//
	// События `audio_recap.*` — про конкретную фичу, и слать их за модульный
	// замок было бы порчей каталога аналитики. Обобщённого события «показан
	// платный замок» в каталоге (106 шт.) сегодня нет; заводить его — отдельное
	// решение, а не побочный эффект этой правки.
	const isFeatureCatalogLock = featureKey === "audio-recap";
	useEffect(() => {
		if (!isFeatureCatalogLock) return;
		track("audio_recap.paywall_shown", { entryPoint });
	}, [entryPoint, isFeatureCatalogLock]);

	const handleUpgrade = (): void => {
		if (isFeatureCatalogLock) track("audio_recap.paywall_cta_clicked", { entryPoint });
		openUpgrade({ kind: "feature-locked", featureKey, targetTier });
	};

	const isCompact = entryPoint === "sidebar";

	/*
	 * Когда продажа скрыта (мобильная сборка до IAP), тизер перестаёт быть
	 * апселлом: App Store 3.1.1 запрещает показывать подписку, пока её нельзя
	 * купить через IAP, а «Аудио-резюме — доступно на Plus» и «Перейти на Plus»
	 * — это ровно она. Остаётся честная констатация «здесь пока пусто», без
	 * кнопки: вести ей некуда, а вежливый тост вместо действия — обман кнопки.
	 */
	const title = commerceVisible
		? (copy?.title ?? t(`paywall.feature.${featureKey}.title`))
		: t("noSale.lockedTitle");
	const description = commerceVisible
		? (copy?.description ?? t(`paywall.feature.${featureKey}.description`))
		: t("noSale.lockedDescription");

	return (
		<div
			className={[
				"rounded-[var(--radius-panel)] border border-border bg-card text-center shadow-e2",
				isCompact ? "p-5" : "p-8",
				className ?? "",
			].join(" ")}
			data-feature={featureKey}
			data-entry={entryPoint}
		>
			<div
				className={[
					"mx-auto flex items-center justify-center rounded-[var(--radius-tile)] bg-primary-soft text-primary [box-shadow:inset_0_1px_0_rgb(255_255_255/0.4)]",
					isCompact ? "size-10" : "size-12",
				].join(" ")}
			>
				<IconLock size={isCompact ? 20 : 24} stroke={1.75} />
			</div>
			<h3
				className={[
					"font-semibold tracking-tight text-foreground",
					isCompact ? "mt-3 text-base" : "mt-4 text-lg",
				].join(" ")}
			>
				{title}
			</h3>
			<p
				className={[
					"mx-auto text-sm text-muted-foreground",
					isCompact ? "mt-1.5 max-w-xs" : "mt-2 max-w-sm",
				].join(" ")}
			>
				{description}
			</p>
			{commerceVisible ? (
				<Button onClick={handleUpgrade} className="mt-4 cursor-pointer">
					{copy ? copy.cta : t(`paywall.feature.${featureKey}.cta`)}
				</Button>
			) : null}
		</div>
	);
}
